import { useMemo } from 'react'
import { buildSupportsByElementIndex, getSupportsOfElement } from '@domainUtils'
import { useSlabBeamToAssembly } from '@hooks'
import { groupBy, keyBy } from 'lodash-es'
import { resolveResults2DetailPaneCrossSection } from '@editor/pages/Results2/components/Results2DetailPane/utils'
import { useResultsStore, useTransmissionStore } from '@editor/stores'
import { useElementCrossSectionAssignment, useElementTypes } from '@editorHooks'
import { SupportingElementCSConfig } from '@queries'
import { MultiEditCheckSettingsElement } from '../types'

/**
 * Resolves the selected members into the shape every check settings multi-edit
 * section works with. Members whose check settings have not loaded are dropped.
 */
const useMultiEditCheckSettingElements = (
  elementGuids: string[],
): MultiEditCheckSettingsElement[] => {
  const memberCheckSettings = useResultsStore(state => state.memberCheckSettings)
  const structuralChecks = useResultsStore(state => state.structuralChecks)

  const elementCrossSectionAssignment = useElementCrossSectionAssignment()
  const verticalGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const elementTypes = useElementTypes()
  const slabBeamGuidToAssembly = useSlabBeamToAssembly()

  const supportsIndex = useMemo(
    () => (verticalGraph ? buildSupportsByElementIndex(verticalGraph) : undefined),
    [verticalGraph],
  )

  // Index the project-wide arrays once per change instead of re-scanning them for
  // every selected element below (mirrors the same indexing already done for these
  // exact arrays in useResults2TabErrorStatuses).
  const checkSettingsByGuid = useMemo(
    () => keyBy(memberCheckSettings, 'element_guid'),
    [memberCheckSettings],
  )
  const assignmentByGuid = useMemo(
    () => keyBy(elementCrossSectionAssignment, 'element_guid'),
    [elementCrossSectionAssignment],
  )
  const checksByGuid = useMemo(
    () => groupBy(structuralChecks ?? [], 'element_guid'),
    [structuralChecks],
  )

  return useMemo(
    () =>
      elementGuids.flatMap(guid => {
        const checkSettings = checkSettingsByGuid[guid]
        // Only build rows for members whose check settings have loaded — never
        // coerce a missing result into SettingsOnMember, which would otherwise
        // feed the equality check, normalization and the submit payload with junk.
        if (!checkSettings) return []

        const supportingElementCSConfig: SupportingElementCSConfig[] = verticalGraph
          ? getSupportsOfElement(guid, verticalGraph, supportsIndex)
              .map(({ guid: supportingGuid }) => {
                const assignment = assignmentByGuid[supportingGuid]
                return assignment
                  ? { element_guid: assignment.element_guid, cross_section: assignment.element_cs }
                  : null
              })
              .filter((c): c is SupportingElementCSConfig => c != null)
          : []

        // Resolve like the single detail pane: slab/roof-slab beams have no direct
        // cross-section assignment — their cross section is derived from the slab's assembly.
        const crossSection = resolveResults2DetailPaneCrossSection({
          elementId: guid,
          elementType: elementTypes[guid],
          elementCrossSectionAssignment,
          slabBeamGuidToAssembly,
        })

        return [
          {
            guid,
            elementType: elementTypes[guid],
            checks: checksByGuid[guid] ?? [],
            checkSettings,
            crossSection,
            supportingElementCSConfig,
          },
        ]
      }),
    [
      elementGuids,
      checksByGuid,
      checkSettingsByGuid,
      assignmentByGuid,
      elementCrossSectionAssignment,
      slabBeamGuidToAssembly,
      verticalGraph,
      supportsIndex,
      elementTypes,
    ],
  )
}

export default useMultiEditCheckSettingElements
